const DEFAULT_API_URL = 'https://api.casparser.in';

export interface ApiClient {
  baseUrl: string;
  apiKey: string;
}

/**
 * Creates an API client for making requests to CASParser API.
 * 
 * @param accessToken - Your access token (get one at casparser.in/docs)
 * @param baseUrl - Optional custom API base URL
 * @returns API client instance
 */
export function createApiClient(accessToken: string, baseUrl?: string): ApiClient {
  return {
    baseUrl: baseUrl || DEFAULT_API_URL,
    apiKey: accessToken,
  };
}

export interface AccessTokenResponse {
  access_token: string;
  token_type: 'api_key';  // Drop-in replacement for x-api-key header
  expires_in: number;
}

/**
 * Request a short-lived access token from the API key.
 * Use this on your backend to generate tokens for frontend use.
 * 
 * @param apiKey - Your API key (do NOT expose this on frontend)
 * @param expiryMinutes - Token validity in minutes (max 60, default 60)
 * @param authUrl - Optional custom auth URL
 * @returns Access token response with `at_` prefixed token
 */
export async function requestAccessToken(
  apiKey: string,
  expiryMinutes: number = 60,
  authUrl?: string
): Promise<AccessTokenResponse> {
  const url = authUrl || DEFAULT_API_URL;
  
  const response = await fetch(`${url}/v1/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: JSON.stringify({
      expiry_minutes: Math.min(expiryMinutes, 60),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || 'Failed to get access token');
  }

  return response.json();
}

/**
 * True if `token` is a short-lived access token (prefix `at_`, minted
 * by `POST /v1/token`). Raw API keys don't use this prefix. Match is
 * case-insensitive to tolerate upper/mixed-case inputs.
 */
export function isAccessToken(token: string): boolean {
  if (!token) return false;
  return token.toLowerCase().startsWith('at_');
}

export async function parseStatement(
  client: ApiClient,
  file: File,
  password?: string,
  // Reserved for future XHR-based upload-progress support. fetch()
  // doesn't expose request-body progress; the orchestrator drives a
  // simulated progress curve in the meantime.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _onProgress?: (percent: number) => void,
): Promise<any> {
  const formData = new FormData();
  formData.append('pdf_file', file);
  if (password) {
    formData.append('password', password);
  }

  const response = await fetch(`${client.baseUrl}/v4/smart/parse`, {
    method: 'POST',
    headers: {
      'x-api-key': client.apiKey,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Request failed' };
  }

  return response.json();
}

/**
 * Parse a statement from a URL (used for CDSL fetch results)
 */
export async function parseStatementFromUrl(
  client: ApiClient,
  pdfUrl: string,
  password?: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _onProgress?: (percent: number) => void,
): Promise<any> {
  const body: Record<string, string> = { pdf_url: pdfUrl };
  if (password) {
    body.password = password;
  }

  const response = await fetch(`${client.baseUrl}/v4/smart/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': client.apiKey,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Request failed' };
  }

  return response.json();
}

export interface GeneratorOptions {
  fromDate?: string;
  toDate?: string;
  password?: string;
  /**
   * Which RTA portal drives the mailback. Defaults to `kfintech`.
   * Both `kfintech` and `cams` retrieve data from both RTAs — the
   * choice only affects which portal hosts the mailback request.
   */
  casAuthority?: 'kfintech' | 'cams';
}

/**
 * Default password we ask the registrar to encrypt the generated CAS
 * PDF with when the embedder doesn't supply one. Exported so the
 * widget can pre-fill the same value on the upload screen — otherwise
 * the user would land on an empty password field even though their
 * CAS is encrypted with this exact string.
 *
 * Keep this a single source of truth: don't duplicate the literal
 * elsewhere in the codebase.
 */
export const GENERATOR_DEFAULT_PASSWORD = 'Abcdefghi12$';

export async function generateCAS(
  client: ApiClient,
  email: string,
  pan: string,
  options?: GeneratorOptions
): Promise<any> {
  // Format dates as YYYY-MM-DD per API spec
  const today = new Date();
  const toDate = options?.toDate || today.toISOString().split('T')[0];
  const fromDate = options?.fromDate || '2000-01-01';
  const password = options?.password || GENERATOR_DEFAULT_PASSWORD;
  const casAuthority = options?.casAuthority || 'kfintech';

  // Using the /v4/generate endpoint from portfolio-tracking
  const response = await fetch(`${client.baseUrl}/v4/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': client.apiKey,
    },
    body: JSON.stringify({
      email,
      pan_no: pan,
      from_date: fromDate,
      to_date: toDate,
      password,
      cas_authority: casAuthority,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || errorData.message || 'Request failed' };
  }

  return response.json();
}

// ============================================
// CDSL Fetch API (2-step OTP flow)
// ============================================

export interface CdslFetchResponse {
  status: string;
  session_id: string;
  msg: string;
}

export interface CdslVerifyResponse {
  status: string;
  msg: string;
  files: Array<{ filename: string; url: string }>;
}

export async function cdslFetchRequestOtp(
  client: ApiClient,
  pan: string,
  boId: string,
  dob: string
): Promise<CdslFetchResponse> {
  const response = await fetch(`${client.baseUrl}/v4/cdsl/fetch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': client.apiKey,
    },
    body: JSON.stringify({
      pan: pan.toUpperCase(),
      bo_id: boId,
      dob,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Failed to request OTP' };
  }

  return response.json();
}

export async function cdslFetchVerifyOtp(
  client: ApiClient,
  sessionId: string,
  otp: string,
  numPeriods: number = 6
): Promise<CdslVerifyResponse> {
  const response = await fetch(`${client.baseUrl}/v4/cdsl/fetch/${sessionId}/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': client.apiKey,
    },
    body: JSON.stringify({
      otp,
      num_periods: numPeriods,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Failed to verify OTP' };
  }

  return response.json();
}

// ============================================
// Inbound Email API (email forwarding flow)
// ============================================

export interface InboundEmailCreateOptions {
  callbackUrl?: string;
  allowedSources?: ('cdsl' | 'nsdl' | 'cams' | 'kfintech')[];
  reference?: string;
  metadata?: Record<string, string>;
  alias?: string;
}

export interface InboundEmailCreateResponse {
  status: string;
  inbound_email_id: string;
  email: string;
  callback_url?: string;
  sdk_mode?: boolean;
  allowed_sources?: string[];
  reference?: string;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface InboundEmailFileResponse {
  message_id: string;
  filename: string;
  original_filename: string;
  message_date: string;
  cas_type: 'cdsl' | 'nsdl' | 'cams' | 'kfintech';
  sender_email?: string | null;
  size: number;
  url: string;
  expires_in: number;
  received_at: string;
}

export interface InboundEmailFilesListResponse {
  status: string;
  files: InboundEmailFileResponse[];
  count: number;
  cursor: string;
}

/**
 * Create a new inbound email address. Omit `callbackUrl` to run in SDK mode
 * (files become pollable; no webhook is fired).
 */
export async function createInboundEmail(
  client: ApiClient,
  options: InboundEmailCreateOptions = {}
): Promise<InboundEmailCreateResponse> {
  const body: Record<string, unknown> = {};
  if (options.callbackUrl) body.callback_url = options.callbackUrl;
  if (options.allowedSources?.length) body.allowed_sources = options.allowedSources;
  if (options.reference) body.reference = options.reference;
  if (options.metadata) body.metadata = options.metadata;
  if (options.alias) body.alias = options.alias;

  const response = await fetch(`${client.baseUrl}/v4/inbound-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': client.apiKey,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Surface the HTTP status so callers can branch on
    // status-code semantics (400/422 = config-validation rejection,
    // 403 = feature unavailable on this tier, etc.) instead of
    // string-matching the API's English error text.
    throw {
      response: { status: response.status, data: errorData },
      status: response.status,
      message: errorData.msg || 'Failed to create inbound email',
    };
  }

  return response.json();
}

/**
 * Poll for files received at an inbound email address. Pass the `cursor` from
 * the previous response as `since` to fetch only new files.
 */
export async function listInboundEmailFiles(
  client: ApiClient,
  inboundEmailId: string,
  since?: string,
  limit: number = 20
): Promise<InboundEmailFilesListResponse> {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  if (limit) params.set('limit', String(limit));
  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(
    `${client.baseUrl}/v4/inbound-email/${encodeURIComponent(inboundEmailId)}/files${query}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': client.apiKey,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Failed to list inbound email files' };
  }

  return response.json();
}

/**
 * Delete an inbound email address. Called by the SDK on widget close in
 * SDK-managed mode to immediately free the slot (otherwise it expires via TTL).
 */
export async function deleteInboundEmail(
  client: ApiClient,
  inboundEmailId: string
): Promise<{ status: string; msg?: string }> {
  const response = await fetch(
    `${client.baseUrl}/v4/inbound-email/${encodeURIComponent(inboundEmailId)}`,
    {
      method: 'DELETE',
      headers: {
        'x-api-key': client.apiKey,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { response: { data: errorData }, message: errorData.msg || 'Failed to delete inbound email' };
  }

  return response.json();
}
